<script setup lang="ts">
import { throttle } from 'lodash-es'
import { onUnmounted } from 'vue'
import {
  useAdminStore,
  useAuthStore,
  useCommonStore,
  useCouponStore,
  useHouseMapStore,
  useRentalStore,
  useTenantStore,
} from './store'
import { isLaunchFinished } from './utils/common'
import { ensureDefaultShare, resetShareForCurrentPage } from './hooks/useWxSdk'

const tenantStore = useTenantStore() // 通用配置
const commonStore = useCommonStore() // 通用配置
// const { hideMenuItems } = useWxSdk()
const { handleTokenExpiry } = useTokenUtils() // 自定义 Hook 处理 Token
const { initWxSdk } = useWxSdk()
const { getLogoImg } = useLogo() // 调用获取 logo 图片逻辑


/** 浏览器恢复历史文档时，所有页面都先同步认证，避免旧内存覆盖新的登录/退出结果。 */
const restoreAuthSession = (event: PageTransitionEvent) => {
  console.info('[auth] pageshow', { persisted: event.persisted })
  if (event.persisted) useAuthStore().restoreAuthInfo()
}
window.addEventListener('pageshow', restoreAuthSession)
onUnmounted(() => window.removeEventListener('pageshow', restoreAuthSession))

/** SPA 浏览器前进/后退不经过 uni 路由拦截，需要手动恢复当前页面的分享默认值。 */
const resetShareOnHistoryChange = () => {
  setTimeout(resetShareForCurrentPage, 0)
}
window.addEventListener('popstate', resetShareOnHistoryChange)
onUnmounted(() => window.removeEventListener('popstate', resetShareOnHistoryChange))

const configWxJsApi = async (): Promise<void> => {
  try {
    // 接口清单与开放标签由 useWxSdk 统一维护，页面分享会自动等待这次初始化。
    await initWxSdk()
    // 当前页没有登记分享内容时，先补一份默认分享，避免用户快速点开菜单时是空白/旧数据。
    ensureDefaultShare()
  } catch (err) {
    console.error('[wx] 微信 JS-SDK 初始化失败:', err)
  }
}
// 重置登录态
const resetAuthInfo = throttle(
  () => {
    const authStore = useAuthStore()
    const adminStore = useAdminStore()
    const commonStore = useCommonStore()
    const rentalStore = useRentalStore()
    const couponStore = useCouponStore()
    const tenantStore = useTenantStore()
    const mapStore = useHouseMapStore()
    authStore.clearAuthInfo()
    adminStore.clearAdminInfo()
    rentalStore.resetState()
    couponStore.resetState()
    commonStore.resetState()
    mapStore.resetState()
    tenantStore.resetState()
  },
  500,
  { trailing: false },
)

onLaunch(async (options) => {
  const tenantId = tenantStore.getTenantData('tenantId')
  // 启动参数可能为空；只有传入租户时才核对并切换认证上下文。
  const launchTenantId = options?.query?.tenant_id
  if (launchTenantId) {
    if (tenantId !== launchTenantId) {
      resetAuthInfo()
    }
    tenantStore.setTenantData('tenantId', launchTenantId)
  }

  isLaunchFinished.value = true // 标记应用启动完成

  handleTokenExpiry()

  configWxJsApi()

  getLogoImg()
  // 重置pinia
  commonStore.resetState()
})
</script>

<style lang="scss">
/**
 * 40035 会员到期弹窗自己承担提示，期间屏蔽页面级 toast（页面仍会 toast.error(res.message)），
 * 避免一次失败同时出现弹窗和 toast。类名由 `src/hooks/usePlanExpiredPrompt.ts` 开关。
 */
body.plan-expired-silence .wd-toast {
  display: none !important;
}

/* 隐藏所有滚动条 app小程序不支持*选择器 */
* {
  -ms-overflow-style: none;
  /* IE and Edge */
  scrollbar-width: none;
  /* Firefox */
}

/* Hide scrollbar for Webkit browsers (Chrome, Safari) */
*::-webkit-scrollbar {
  display: none;
}

.uni-tabbar {
  display: none;
}

.uni-tabbar__item {
  display: none;
}

.uni-tabbar-border {
  display: none;
}

button::after {
  border: none;
}

swiper,
scroll-view {
  flex: 1;
  height: 100%;
  overflow: hidden;
}

image {
  width: 100%;
  height: 100%;
  vertical-align: middle;
}

// 单行省略，优先使用 unocss: text-ellipsis
// 两行省略 line-clamp-2
.ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// 注意 平板下拉刷新行内样式是写死的，这里写在class上可以让px2rem转成rem
:deep(.uni-page-refresh) {
  clip: rect(-84px, 45px, 45px, -5px) !important;
}

//该样式主要用语HouseMap.vue
// 这些样式用于地图上的标记（poi）时，标记内容是动态生成的 HTML 字符串，不在 Vue 模板中直接渲染，无法使用 scoped 样式。全局样式确保 .map-poi-talk-bubble-default 和 .map-poi-talk-bubble-selected 能应用于动态生成的内容。
.map-poi-talk-bubble-default {
  --h: 8px; // CSS 变量定义，单位需要在这里声明
  position: relative;
  z-index: 3;
  padding: 5px 10px;
  font-size: 14px;
  white-space: nowrap;
  /* 禁止换行 */
  background: #fff;
  border: 1px solid #ededed;
  border-radius: 8px;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.3);
  opacity: 0.9;
  transform: translate(-50%, calc(-100% - var(--h)));

  &::before {
    position: absolute;
    bottom: calc(-1 * var(--h));
    /* 正确的变量引用 */
    left: 50%;
    z-index: -1;
    width: 0;
    height: 0;
    content: '';
    border-top: var(--h) solid #fff !important;
    /* 边框颜色 */
    border-right: var(--h) solid transparent;
    border-left: var(--h) solid transparent;
    transform: translateX(-50%);
  }
}

.map-poi-talk-bubble-selected {
  --h: 8px; // CSS 变量定义，单位需要在这里声明
  position: relative;
  z-index: 3;
  padding: 5px 10px;
  font-size: 14px;
  color: #fff;
  white-space: nowrap;
  /* 禁止换行 */
  background: var(--wat-main-color) !important;
  border-radius: 8px;
  opacity: 0.9;
  transform: translate(-50%, calc(-100% - var(--h)));

  &::before {
    position: absolute;
    bottom: calc(-1 * var(--h));
    /* 正确的变量引用 */
    left: 50%;
    z-index: -1;
    width: 0;
    height: 0;
    content: '';
    border-top: var(--h) solid var(--wat-main-color) !important;
    /* 边框颜色 */
    border-right: var(--h) solid transparent;
    border-left: var(--h) solid transparent;
    transform: translateX(-50%);
  }
}
</style>
